/**
 * Solution page stats section: "Proven Results That Drive Growth" style.
 *
 * Renders when a Solution page's data includes a `stats` object. Numeric
 * values roll up on scroll-into-view; string values (e.g. "Thousands")
 * render as-is with a subtle fade.
 *
 * Usage in a solution's data:
 *   stats: {
 *     eyebrow: "REAL RESULTS. REAL IMPACT.",
 *     heading: "Proven Results That Drive Growth",
 *     intro: "...",
 *     items: [
 *       { icon: "trending-up", value: 40, suffix: "%", label: "increase in..." },
 *       { icon: "gauge",       value: "Thousands", label: "of manual tasks automated" },
 *     ],
 *     footer_note: "These are the results one customer achieved. How much can you improve?"
 *   }
 */
import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import {
  TrendingUp, Gauge, Calculator, ClipboardCheck, Sparkles, Award,
  BarChart3, CircleDollarSign, Clock, Zap, Users, Target,
} from "lucide-react";

const ICON_MAP = {
  "trending-up": TrendingUp,
  "gauge": Gauge,
  "calculator": Calculator,
  "clipboard-check": ClipboardCheck,
  "sparkles": Sparkles,
  "award": Award,
  "bar-chart": BarChart3,
  "dollar": CircleDollarSign,
  "clock": Clock,
  "zap": Zap,
  "users": Users,
  "target": Target,
};

function CountUp({ end, duration = 1400 }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });

  useEffect(() => {
    if (!inView) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / duration);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(end * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);

  return <span ref={ref}>{value}</span>;
}

export default function SolutionStats({ stats }) {
  if (!stats || !Array.isArray(stats.items) || stats.items.length === 0) return null;

  return (
    <section
      data-testid="solution-stats-section"
      className="py-14 lg:py-20 bg-gradient-to-b from-slate-50 to-white"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10 lg:mb-14">
          {stats.eyebrow && (
            <span
              data-testid="solution-stats-eyebrow"
              className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-sky-600"
            >
              {stats.eyebrow}
            </span>
          )}
          <h2
            data-testid="solution-stats-heading"
            className="mt-3 text-3xl sm:text-4xl lg:text-[2.5rem] font-extrabold tracking-tight text-slate-900 leading-[1.15]"
          >
            {stats.heading}
          </h2>
          {stats.intro && (
            <p className="mt-4 text-base text-slate-500 leading-relaxed">
              {stats.intro}
            </p>
          )}
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-10">
          <div
            className={
              stats.items.length === 3
                ? "grid gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-3"
                : "grid gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
            }
          >
            {stats.items.map((item, i) => {
              const Icon = ICON_MAP[item.icon] || TrendingUp;
              // Numeric stats roll up via CountUp; text stats render as-is.
              // Since admin edits save the value as a string, also accept a
              // string that parses cleanly to a number (e.g. "40", "1.5").
              const raw = item.value;
              const numeric =
                typeof raw === "number" ? raw :
                (typeof raw === "string" && /^-?\d+(\.\d+)?$/.test(raw.trim())) ? Number(raw) :
                null;
              const isNumeric = numeric !== null && !Number.isNaN(numeric);
              return (
                <motion.div
                  key={`${item.label}-${i}`}
                  data-testid={`solution-stat-${i}`}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.08, duration: 0.4 }}
                  className="text-center"
                >
                  <span className="inline-flex w-12 h-12 rounded-xl bg-sky-50 ring-1 ring-sky-100 items-center justify-center mb-3">
                    <Icon className="h-6 w-6 text-sky-600" strokeWidth={2.25} />
                  </span>
                  <div
                    className="text-4xl lg:text-5xl font-extrabold text-sky-600 tracking-tight"
                    data-testid={`solution-stat-${i}-value`}
                  >
                    {isNumeric ? (
                      <>
                        <CountUp end={numeric} />
                        {item.suffix || ""}
                      </>
                    ) : (
                      item.value
                    )}
                  </div>
                  <p className="mt-2 text-sm text-slate-600 leading-snug max-w-[16rem] mx-auto whitespace-pre-line">
                    {item.label}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {stats.footer_note && (
            <div className="mt-8 pt-6 border-t border-slate-100 flex items-start sm:items-center gap-3 text-sm text-slate-600 justify-center">
              <span className="w-8 h-8 rounded-full bg-slate-900 ring-2 ring-white flex items-center justify-center shrink-0">
                <Award className="h-4 w-4 text-amber-400" strokeWidth={2.25} />
              </span>
              <p data-testid="solution-stats-footer-note">
                {stats.footer_note}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
